/**
 * SupportTicketsPage — coda ticket di assistenza dell'operatore (rotta
 * /ticket). Lista CROSS-TENANT di `support_tickets` ordinata per ultimo
 * messaggio, con filtri client-side, metriche di testata e drawer di
 * dettaglio con thread + risposta.
 *
 * Metriche di testata (calcolate sui ticket caricati) — SEPARATE:
 * - tempo medio di PRIMA RISPOSTA (created_at → first_response_at): reattività;
 * - tempo medio di RISOLUZIONE (created_at → resolved_at): soluzione.
 *
 * Estetica: coerente con PlatformLeadsPage (console editoriale telemetrica).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, CircularProgress, IconButton, InputAdornment, MenuItem, Paper,
  Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography, alpha,
} from '@mui/material';
import { Refresh, Search } from '@mui/icons-material';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { TicketDetailDrawer } from '../components/support/TicketDetailDrawer';
import {
  TICKET_CATEGORIA_LABELS, TICKET_CATEGORIA_ORDER, TICKET_PRIORITA_LABELS,
  TICKET_PRIORITA_ORDER, TICKET_PRIORITA_TONES, TICKET_STATO_LABELS,
  TICKET_STATO_ORDER, TICKET_STATO_TONES, formatDate, formatDurataMs, tsMillis,
} from '../components/support/ticketLabels';
import { listSupportTickets } from '../services/supportTicketsService';
import type {
  SupportTicket, SupportTicketCategoria, SupportTicketPriorita, SupportTicketStato,
} from '../types/supportTickets';

/** Ticket che aspetta una risposta dell'operatore (ultimo messaggio del cliente). */
function isDaRispondere(t: SupportTicket): boolean {
  return t.last_message_by === 'cliente' && t.stato !== 'chiuso';
}

/**
 * Media in ms di una durata created_at → `getEnd(t)` sui soli ticket che
 * hanno ENTRAMBI i timestamp (null se nessun campione: mai medie inventate).
 */
function averageDurationMs(
  tickets: SupportTicket[],
  getEnd: (t: SupportTicket) => unknown,
): number | null {
  let sum = 0;
  let count = 0;
  for (const t of tickets) {
    const startMs = tsMillis(t.created_at);
    const endMs = tsMillis(getEnd(t));
    if (startMs === null || endMs === null) continue;
    const delta = endMs - startMs;
    if (delta < 0) continue;
    sum += delta;
    count += 1;
  }
  return count > 0 ? sum / count : null;
}

export const SupportTicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState<SupportTicketStato | 'all'>('all');
  const [filterCategoria, setFilterCategoria] = useState<SupportTicketCategoria | 'all'>('all');
  const [filterPriorita, setFilterPriorita] = useState<SupportTicketPriorita | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setTickets(await listSupportTickets());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento ticket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      if (filterStato !== 'all' && t.stato !== filterStato) return false;
      if (filterCategoria !== 'all' && t.categoria !== filterCategoria) return false;
      if (filterPriorita !== 'all' && t.priorita !== filterPriorita) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const haystack = [
          t.numero, t.tenant_nome, t.oggetto, t.descrizione,
          t.created_by_nome, t.created_by_email,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, filterStato, filterCategoria, filterPriorita]);

  // Metriche di testata — sui ticket CARICATI (non filtrati)
  const metrics = useMemo(() => {
    let aperti = 0;
    let inLavorazione = 0;
    let inAttesaCliente = 0;
    for (const t of tickets) {
      if (t.stato === 'aperto') aperti += 1;
      else if (t.stato === 'in_lavorazione') inLavorazione += 1;
      else if (t.stato === 'in_attesa_cliente') inAttesaCliente += 1;
    }
    return {
      aperti,
      inLavorazione,
      inAttesaCliente,
      // Due medie DIVERSE: reattività (prima risposta) vs soluzione (risoluzione)
      mediaPrimaRispostaMs: averageDurationMs(tickets, (t) => t.first_response_at),
      mediaRisoluzioneMs: averageDurationMs(tickets, (t) => t.resolved_at),
    };
  }, [tickets]);

  const selectedTicket = useMemo(
    () => tickets.find((t) => t.id === selectedId) ?? null,
    [tickets, selectedId],
  );

  const showSnack = (msg: string, severity: 'success' | 'error') => setSnack({ msg, severity });

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontWeight: 600,
              fontSize: { xs: '1.6rem', md: '1.95rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: tokens.color.graphite,
              fontVariationSettings: '"WONK" 0, "SOFT" 30',
            }}
          >
            Ticket
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 540 }}>
            Coda di assistenza cross-tenant: richieste dei clienti, tempi di prima
            risposta e di risoluzione, thread di conversazione.
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.5 }}>
          <HeaderMetric
            label="Aperti"
            value={metrics.aperti.toString()}
            accentColor={tokens.color.warning}
          />
          <HeaderMetric
            label="In lavorazione"
            value={metrics.inLavorazione.toString()}
            accentColor={tokens.color.info}
          />
          <HeaderMetric
            label="In attesa cliente"
            value={metrics.inAttesaCliente.toString()}
            accentColor={tokens.color.tierBusiness.main}
          />
          <HeaderMetric
            label="Prima risposta (media)"
            value={formatDurataMs(metrics.mediaPrimaRispostaMs)}
          />
          <HeaderMetric
            label="Risoluzione (media)"
            value={formatDurataMs(metrics.mediaRisoluzioneMs)}
          />
          <Tooltip title="Ricarica">
            <IconButton onClick={load} disabled={loading} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* FILTRI */}
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            placeholder="Cerca per numero, azienda, oggetto o richiedente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: tokens.color.mist }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.85rem' },
            }}
          />
          <TextField
            select size="small" value={filterStato}
            onChange={(e) => setFilterStato(e.target.value as SupportTicketStato | 'all')}
            sx={{ minWidth: 190, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutti gli stati</MenuItem>
            {TICKET_STATO_ORDER.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>{TICKET_STATO_LABELS[s]}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value as SupportTicketCategoria | 'all')}
            sx={{ minWidth: 200, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutte le categorie</MenuItem>
            {TICKET_CATEGORIA_ORDER.map((c) => (
              <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{TICKET_CATEGORIA_LABELS[c]}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" value={filterPriorita}
            onChange={(e) => setFilterPriorita(e.target.value as SupportTicketPriorita | 'all')}
            sx={{ minWidth: 170, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutte le priorità</MenuItem>
            {TICKET_PRIORITA_ORDER.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: '0.85rem' }}>{TICKET_PRIORITA_LABELS[p]}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {/* TABELLA */}
      <Paper
        variant="outlined"
        sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}
      >
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Table size="small" sx={{
            '& td': { py: 1.5 },
            '& tr:last-child td': { borderBottom: 0 },
            '& tbody tr': { transition: 'background-color 120ms ease', cursor: 'pointer' },
          }}>
            <TableHead>
              <TableRow>
                <TableCell>Numero</TableCell>
                <TableCell>Azienda</TableCell>
                <TableCell>Oggetto</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Priorità</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Ultimo messaggio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((t) => {
                const daRispondere = isDaRispondere(t);
                return (
                  <TableRow
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    sx={daRispondere
                      // Evidenza "da rispondere": tinta ambra sull'intera riga
                      ? {
                        backgroundColor: alpha(tokens.color.warning, 0.05),
                        '&:hover': { backgroundColor: alpha(tokens.color.warning, 0.1) },
                      }
                      : { '&:hover': { backgroundColor: tokens.color.bone } }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box component="span" sx={{
                        fontFamily: tokens.font.mono,
                        fontSize: '0.78rem',
                        color: tokens.color.ash,
                        letterSpacing: '0.02em',
                      }}>
                        {t.numero}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>
                        {t.tenant_nome}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 240 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: daRispondere ? 600 : 400,
                            color: tokens.color.graphite,
                            lineHeight: 1.3,
                          }}
                        >
                          {t.oggetto}
                        </Typography>
                        {daRispondere && <SoftChip label="Da rispondere" tone="warning" />}
                      </Stack>
                      <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                        {t.created_by_nome}
                        {/*
                          ⚠️ Chi legge deve sapere che quelle sono parole DETTE a un assistente
                          mentre la persona lavorava, non un testo scritto per essere letto: si
                          risponde in un altro modo. La pagina dice dove si trovava.
                        */}
                        {t.origine === 'yuma' && ` · 🐱 chiesto a Yuma${t.rotta ? ` in ${t.rotta}` : ''}`}
                        {typeof t.messaggi_count === 'number' && ` · ${t.messaggi_count} msg`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SoftChip label={TICKET_CATEGORIA_LABELS[t.categoria] ?? t.categoria} tone="info" />
                    </TableCell>
                    <TableCell>
                      <SoftChip
                        label={TICKET_PRIORITA_LABELS[t.priorita] ?? t.priorita}
                        tone={TICKET_PRIORITA_TONES[t.priorita] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <SoftChip
                        label={TICKET_STATO_LABELS[t.stato] ?? t.stato}
                        tone={TICKET_STATO_TONES[t.stato] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}>
                      <Box component="span" sx={{
                        fontFamily: tokens.font.mono,
                        fontSize: '0.75rem',
                        color: tokens.color.mist,
                      }}>
                        {formatDate(t.last_message_at ?? t.created_at)}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography
                      sx={{
                        fontFamily: tokens.font.display,
                        fontSize: '1.05rem',
                        fontWeight: 500,
                        fontStyle: 'italic',
                        color: tokens.color.mist,
                      }}
                    >
                      {tickets.length === 0
                        ? 'Nessun ticket di assistenza: la coda è vuota.'
                        : 'Nessun ticket corrisponde ai filtri.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Drawer dettaglio */}
      <TicketDetailDrawer
        ticket={selectedTicket}
        onClose={() => setSelectedId(null)}
        onChanged={load}
        showSnack={showSnack}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

// ============================================================================
// HEADER METRIC — pill compatta coerente con PlatformLeadsPage
// ============================================================================

const HeaderMetric: React.FC<{ label: string; value: string; accentColor?: string }> = ({
  label, value, accentColor,
}) => (
  <Box sx={{ borderLeft: `2px solid ${accentColor ?? tokens.color.line}`, pl: 1.5 }}>
    <Typography
      sx={{
        fontFamily: tokens.font.body,
        fontSize: '0.62rem',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.color.mist,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: tokens.font.display,
        fontSize: '1.15rem',
        fontWeight: 600,
        color: accentColor ?? tokens.color.graphite,
        lineHeight: 1.2,
        mt: 0.25,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Typography>
  </Box>
);
